import { DOCUMENT } from '@angular/common';
import { DestroyRef, Directive, ElementRef, NgZone, inject, input, output } from '@angular/core';

type ResizeEdge = 'top' | 'bottom';

@Directive({
    selector: '[webBlockHeightResize]',
    host: {
        '(pointerdown)': 'onPointerDown($event)',
    },
})
export class BlockHeightResizeDirective {
    private readonly handle = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
    private readonly document = inject(DOCUMENT);
    private readonly zone = inject(NgZone);

    /**
     * The height a drag starts from — read once, on pointerdown, so the heights the
     * caller feeds back through this input can't shift the gesture's origin.
     */
    public readonly height = input<number>(0);

    /** Edge used for resizing. */
    public readonly edge = input<ResizeEdge>('bottom');

    /** Constraints */
    public readonly minHeight = input<number>(0);
    public readonly maxHeight = input<number | null>(null);
    public readonly snap = input<number | null>(null);

    public readonly heightChange = output<number>();
    public readonly heightChangeStart = output<void>();
    public readonly heightChangeEnd = output<void>();

    /**
     * Releases the drag in flight: the guard against a second one, and what the
     * destroy hook calls so a directive torn down mid-drag leaves nothing behind.
     */
    private cleanupDrag: (() => void) | null = null;

    constructor() {
        inject(DestroyRef).onDestroy(() => this.cleanupDrag?.());
    }

    protected onPointerDown(event: PointerEvent): void {
        // A second pointer landing on the handle would start a rival gesture off
        // the same element; the drag already running owns it.
        if (this.cleanupDrag) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        this.handle.setPointerCapture?.(event.pointerId);

        const root = this.document.documentElement;
        const prevUserSelect = root.style.userSelect;
        const controller = new AbortController();
        const startY = event.clientY;
        const startHeight = this.height();

        let pointerY = startY;
        let frame = 0;
        let emitted = startHeight;

        root.style.userSelect = 'none';

        const flush = (): void => {
            frame = 0;

            const height = this.resolveHeight(startHeight, pointerY - startY);

            // Repeats are the rule once the drag sits against a bound or on a snap
            // step, and every emit costs the caller a form write plus a CD pass.
            if (height === emitted) {
                return;
            }

            emitted = height;
            this.zone.run(() => this.heightChange.emit(height));
        };

        const onMove = (moveEvent: PointerEvent): void => {
            pointerY = moveEvent.clientY;

            // Pointermove outruns the display on a high-rate device, and the whole
            // handler ran inside the zone before — a full change detection pass per
            // event. One coalesced emit per frame is all the block can render anyway.
            frame ||= requestAnimationFrame(flush);
        };

        const cleanup = (): void => {
            this.cleanupDrag = null;
            controller.abort();
            cancelAnimationFrame(frame);
            this.handle.releasePointerCapture?.(event.pointerId);
            root.style.userSelect = prevUserSelect;
        };

        const onEnd = (): void => {
            const pending = frame !== 0;

            cleanup();

            // The last move can still be sitting in a frame that never fired.
            if (pending) {
                flush();
            }

            this.zone.run(() => this.heightChangeEnd.emit());
        };

        this.cleanupDrag = cleanup;
        this.heightChangeStart.emit();

        // Outside the zone: only the two emits above and the coalesced heightChange
        // need to reach Angular, not the raw move stream.
        this.zone.runOutsideAngular(() => {
            const options = { signal: controller.signal };

            this.document.addEventListener('pointermove', onMove, options);
            this.document.addEventListener('pointerup', onEnd, options);
            // Without this the drag never ends when the browser takes the pointer
            // away (a system gesture, a lost window): the page would stay
            // unselectable and the caller's transaction open for good.
            this.document.addEventListener('pointercancel', onEnd, options);
        });
    }

    /** The height for a drag of `deltaY` px on the bound edge. */
    private resolveHeight(startHeight: number, deltaY: number): number {
        const snap = this.snap();
        const maxHeight = this.maxHeight();

        let height = this.edge() === 'top' ? startHeight - deltaY : startHeight + deltaY;

        // Snapped before clamping, not after: rounding to the grid last is what let
        // the previous version hand back a height past minHeight/maxHeight.
        if (snap && snap > 1) {
            height = Math.round(height / snap) * snap;
        }

        height = Math.max(this.minHeight(), height);

        if (maxHeight != null) {
            height = Math.min(height, maxHeight);
        }

        return height;
    }
}
